<template>
  <div class="tin-page">
    <div class="ambient"></div>
    <div class="ambient-floor"></div>
    <div class="grain"></div>
    <div class="frame"></div>
    <div class="frame-inner"></div>

    <div class="page-inner wide">

      <!-- Header -->
      <div class="admin-header">
        <div class="wm-row slim">
          <div class="wm-line"></div>
          <span class="wm-text">Club Trailz · Admin</span>
          <div class="wm-line r"></div>
        </div>
        <div class="header-row">
          <h1 class="page-title left">The <em>Waitlist</em></h1>
          <span v-if="counts.pending" class="pending-badge">{{ counts.pending }}</span>
        </div>
      </div>

      <!-- Search -->
      <div class="search-box">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input v-model="search" class="search-input" type="text" placeholder="Search by name, email, or club…" />
      </div>

      <!-- Tabs -->
      <div class="tab-row">
        <button
          v-for="opt in statusOptions"
          :key="opt.value"
          class="tab-btn"
          :class="{ 'tab-btn--active': statusFilter === opt.value }"
          @click="statusFilter = opt.value"
        >
          {{ opt.label }} ({{ counts[opt.value] }})
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="empty-block">
        <div class="seal small"><div class="seal-ring"></div><div class="seal-ring-dashed"></div><div class="seal-diamond"></div></div>
        <p class="empty-text">Loading applications…</p>
      </div>

      <!-- Empty -->
      <div v-else-if="filteredApplicants.length === 0" class="empty-block">
        <div class="empty-diamond"></div>
        <p class="empty-title">{{ statusFilter === 'pending' ? 'All caught up' : 'Nothing here' }}</p>
        <p class="empty-text">No {{ statusFilter === 'all' ? '' : statusFilter }} applications{{ search ? ' match your search' : '' }}.</p>
      </div>

      <!-- Cards -->
      <div v-else class="cards-list">
        <div
          v-for="app in filteredApplicants"
          :key="app.uid"
          class="app-card"
          :class="{ 'app-card--flagged': !app.primaryClub && app.status === 'pending' }"
        >
          <button class="app-card-head" @click="toggle(app.uid)">
            <div class="app-avatar">
              <img v-if="app.image" :src="app.image" alt="" />
              <span v-else>{{ initials(app.fullName) }}</span>
            </div>
            <div class="app-head-info">
              <p class="app-name">{{ app.fullName }}</p>
              <p class="app-club" :class="{ 'app-club--missing': !app.primaryClub }">
                {{ app.primaryClub || 'No club listed' }}
              </p>
            </div>
            <span class="status-chip" :class="'status-chip--' + app.status">{{ app.status }}</span>
            <svg class="chevron" :class="{ 'chevron--open': expanded === app.uid }" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
          </button>

          <div v-if="expanded === app.uid" class="app-card-body">
            <div class="detail-grid">
              <div class="detail-row"><span class="detail-label">Email</span><span class="detail-val">{{ app.email }}</span></div>
              <div class="detail-row"><span class="detail-label">Phone</span><span class="detail-val">{{ app.phone || '—' }}</span></div>
              <div class="detail-row"><span class="detail-label">Submitted</span><span class="detail-val">{{ fmt(app.createdAt) }}</span></div>
              <div v-if="app.jobTitle" class="detail-row"><span class="detail-label">Job Title</span><span class="detail-val">{{ app.jobTitle }}</span></div>
              <div v-if="app.company" class="detail-row"><span class="detail-label">Company</span><span class="detail-val">{{ app.company }}</span></div>
              <div v-if="app.baseCity" class="detail-row"><span class="detail-label">Home Base</span><span class="detail-val">{{ app.baseCity }}</span></div>
              <div v-if="app.frequentCities" class="detail-row"><span class="detail-label">Frequent Cities</span><span class="detail-val">{{ app.frequentCities }}</span></div>
              <div v-if="app.clubs.length > 1" class="detail-row"><span class="detail-label">All Clubs</span><span class="detail-val">{{ app.clubs.map(c => c.name).join(', ') }}</span></div>
              <div v-if="app.referrer" class="detail-row"><span class="detail-label">Referred By</span><span class="detail-val">{{ app.referrer }}</span></div>
            </div>

            <div v-if="app.why" class="why-block">
              <span class="detail-label">Why Club Trailz</span>
              <p class="why-text">{{ app.why }}</p>
            </div>

            <div class="nda-row">
              <svg v-if="app.ndaAccepted" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="nda-ok"><path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/></svg>
              <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="nda-bad"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              <span>{{ app.ndaAccepted ? 'NDA & Code of Conduct accepted' : 'NDA not accepted' }}</span>
            </div>

            <div v-if="app.status === 'pending'" class="action-row">
              <button class="btn-reject" :disabled="busyId === app.uid" @click="confirmReject(app)">Reject</button>
              <button class="btn-approve" :disabled="busyId === app.uid" @click="confirmApprove(app)">Approve</button>
            </div>
            <div v-else class="resolved-row" :class="'resolved-row--' + app.status">
              {{ app.status === 'approved' ? 'Approved' : 'Rejected' }}
              <span v-if="app.reviewer"> · reviewed by admin</span>
            </div>
          </div>
        </div>

        <button v-if="canLoadMore" class="load-more-btn" :disabled="loadingMore" @click="loadMore">
          {{ loadingMore ? 'Loading…' : 'Load More' }}
        </button>
      </div>
    </div>

    <!-- Bottom Tab Bar -->
    <div class="admin-tab-bar">
      <button class="tab-item" :class="{ 'tab-item--active': $route.name === 'AdminHomeView' }" @click="$router.push({ name: 'AdminHomeView' })">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        <span>Home</span>
      </button>
      <button class="tab-item" :class="{ 'tab-item--active': $route.name === 'AdminSearch' }" @click="$router.push({ name: 'AdminSearch' })">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <span>Search</span>
      </button>
      <button class="tab-item" :class="{ 'tab-item--active': $route.name === 'AdminWaitlist' }" @click="$router.push({ name: 'AdminWaitlist' })">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
        <span>Waitlist</span>
      </button>
      <button class="tab-item" :class="{ 'tab-item--active': $route.name === 'AdminFlags' }" @click="$router.push({ name: 'AdminFlags' })">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
        <span>Flags</span>
      </button>
      <button class="tab-item" :class="{ 'tab-item--active': $route.name === 'AdminMessages' }" @click="$router.push({ name: 'AdminMessages' })">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/></svg>
        <span>Messages</span>
      </button>
      <button class="tab-item" :class="{ 'tab-item--active': $route.name === 'AdminSettings' }" @click="$router.push({ name: 'AdminSettings' })">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
        <span>Settings</span>
      </button>
    </div>

    <!-- Confirm dialog -->
    <div v-if="confirmTarget" class="overlay" @click.self="confirmTarget = null">
      <div class="confirm-panel">
        <p class="confirm-title">{{ confirmIsApprove ? 'Approve Application?' : 'Reject Application?' }}</p>
        <p class="confirm-body">
          <strong>{{ confirmTarget.fullName }}</strong>
          {{ confirmIsApprove
            ? 'will be granted full member access and notified by email.'
            : 'will be notified their application was not approved.' }}
        </p>
        <div class="confirm-actions">
          <button class="btn-back" @click="confirmTarget = null">Cancel</button>
          <button :class="confirmIsApprove ? 'btn-approve' : 'btn-reject'" @click="runConfirmedAction">
            {{ confirmIsApprove ? 'Approve' : 'Reject' }}
          </button>
        </div>
      </div>
    </div>

    <transition name="alert-slide">
      <div v-if="snack.show" class="snackbar" :class="'snackbar--' + snack.color">{{ snack.text }}</div>
    </transition>
  </div>
</template>

<script>
import {
  collection, query, orderBy, limit, startAfter, onSnapshot,
  doc, getDocs, serverTimestamp, writeBatch
} from "firebase/firestore";
import { db, auth } from "@/firebase";

// Matches the actual current applications/{uid} schema written by
// WaitlistRegister.vue's register() — the previous mapApplication() here
// referenced app.intellectualProfile / app.conciergeProfile / app.organization,
// fields from an older, more elaborate lifestyle-questionnaire form that no
// longer exists on either platform. Every real application submitted through
// the current form would have shown up here mostly blank.
function mapApplication(app) {
  const clubs = Array.isArray(app.clubs)
    ? app.clubs.filter(c => c && c.name)
    : [];
  const status = app.status === "approved" ? "approved"
    : app.status === "rejected" ? "rejected"
    : "pending"; // covers "pending_review" and anything unset

  return {
    uid: app.uid,
    fullName: app.fullName || "",
    email: (app.email || "").toLowerCase(),
    phone: app.phone || "",
    username: app.username || "",
    clubs,
    primaryClub: clubs[0]?.name || "",
    jobTitle: app.jobTitle || "",
    company: app.company || "",
    baseCity: app.baseCity || "",
    frequentCities: app.frequentCities || "",
    why: app.why || "",
    referrer: app.referrer || "",
    privacyLevel: app.privacyLevel || "Members-only",
    ndaAccepted: !!app.ndaAccepted,
    updates: !!app.updates,
    status,
    createdAt: app.createdAt || null,
    approvedAt: app.approvedAt || null,
    rejectedAt: app.rejectedAt || null,
    reviewer: app.reviewer || null,
    wave: app.wave ?? null,
    image: app.image || null,
    _raw: app,
  };
}

export default {
  name: "AdminWaitlist",
  data() {
    return {
      loading: true,
      loadingMore: false,
      applicants: [],
      lastSnap: null,
      pageSize: 50,
      search: "",
      statusFilter: "pending",
      statusOptions: [
        { value: "pending", label: "Pending" },
        { value: "approved", label: "Approved" },
        { value: "rejected", label: "Rejected" },
        { value: "all", label: "All" },
      ],
      expanded: null,
      busyId: null,
      confirmTarget: null,
      confirmIsApprove: false,
      snack: { show: false, text: "", color: "info" },
      unsub: null,
    };
  },
  computed: {
    filteredApplicants() {
      let items = this.applicants;
      if (this.statusFilter !== "all") {
        items = items.filter(a => a.status === this.statusFilter);
      }
      if (this.search) {
        const s = this.search.toLowerCase();
        items = items.filter(a =>
          a.fullName.toLowerCase().includes(s) ||
          a.email.toLowerCase().includes(s) ||
          a.primaryClub.toLowerCase().includes(s)
        );
      }
      return items;
    },
    counts() {
      const c = { all: this.applicants.length, pending: 0, approved: 0, rejected: 0 };
      this.applicants.forEach(a => { c[a.status]++; });
      return c;
    },
    canLoadMore() { return !!this.lastSnap; },
  },
  mounted() { this.attachLiveQuery(); },
  beforeUnmount() { if (this.unsub) this.unsub(); },
  methods: {
    toast(text, color = "info") {
      this.snack = { show: true, text, color };
      setTimeout(() => { this.snack.show = false; }, 2500);
    },
    initials(name) {
      const n = (name || "").trim();
      if (!n) return "?";
      const parts = n.split(/\s+/).slice(0, 2);
      return parts.map(p => p[0]?.toUpperCase() || "").join("");
    },
    fmt(ts) {
      if (!ts) return "—";
      try { return ts.toDate ? ts.toDate().toLocaleString() : new Date(ts).toLocaleString(); }
      catch { return "—"; }
    },
    toggle(uid) { this.expanded = this.expanded === uid ? null : uid; },

    attachLiveQuery() {
      const qy = query(collection(db, "applications"), orderBy("createdAt", "desc"), limit(this.pageSize));
      if (this.unsub) this.unsub();
      this.unsub = onSnapshot(qy, (snap) => {
        this.applicants = snap.docs.map(d => mapApplication({ uid: d.id, ...d.data() }));
        this.lastSnap = snap.docs.length === this.pageSize ? snap.docs[snap.docs.length - 1] : null;
        this.loading = false;
      }, (err) => {
        console.error(err);
        this.toast("Failed to load applications", "error");
        this.loading = false;
      });
    },

    async loadMore() {
      if (!this.lastSnap) return;
      this.loadingMore = true;
      const qy = query(collection(db, "applications"), orderBy("createdAt", "desc"), startAfter(this.lastSnap), limit(this.pageSize));
      const snap = await getDocs(qy);
      const mapped = snap.docs.map(d => mapApplication({ uid: d.id, ...d.data() }));
      this.applicants = [...this.applicants, ...mapped];
      this.lastSnap = snap.docs.length === this.pageSize ? snap.docs[snap.docs.length - 1] : null;
      this.loadingMore = false;
    },

    confirmApprove(app) { this.confirmTarget = app; this.confirmIsApprove = true; },
    confirmReject(app) { this.confirmTarget = app; this.confirmIsApprove = false; },
    runConfirmedAction() {
      const app = this.confirmTarget;
      this.confirmTarget = null;
      if (this.confirmIsApprove) this.approve(app);
      else this.reject(app);
    },

    /* Approve: updates applications/{uid} and user/{uid} — unchanged from
       before, this part was already correct against the current schema. */
    async approve(item) {
      try {
        this.busyId = item.uid;
        const batch = writeBatch(db);
        batch.set(doc(db, "applications", item.uid), {
          status: "approved", approvedAt: serverTimestamp(), reviewer: auth.currentUser?.uid || null
        }, { merge: true });
        batch.set(doc(db, "user", item.uid), {
          uid: item.uid,
          fullName: item.fullName || "",
          email: item.email || "",
          role: "Member",
          type: "Member",
          status: "approved",
          approvedAt: serverTimestamp(),
        }, { merge: true });
        await batch.commit();
        this.toast("Approved ✔️", "success");
      } catch (e) {
        console.error(e);
        this.toast("Approval failed", "error");
      } finally { this.busyId = null; }
    },

    async reject(item) {
      try {
        this.busyId = item.uid;
        const batch = writeBatch(db);
        batch.set(doc(db, "applications", item.uid), {
          status: "rejected", rejectedAt: serverTimestamp(), reviewer: auth.currentUser?.uid || null
        }, { merge: true });
        batch.set(doc(db, "user", item.uid), {
          uid: item.uid,
          fullName: item.fullName || "",
          email: item.email || "",
          status: "rejected",
          rejectedAt: serverTimestamp()
        }, { merge: true });
        await batch.commit();
        this.toast("Rejected", "info");
      } catch (e) {
        console.error(e);
        this.toast("Reject failed", "error");
      } finally { this.busyId = null; }
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

/* ── SHARED SHELL (same as WaitlistRegister.vue) ── */
.tin-page { position: relative; min-height: 100vh; background: #070708; }
.ambient { position: fixed; top: -300px; left: 50%; transform: translateX(-50%); width: 1000px; height: 800px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 10%, rgba(210,175,100,0.1) 0%, rgba(210,175,100,0.03) 30%, transparent 65%); }
.ambient-floor { position: fixed; bottom: -200px; left: 50%; transform: translateX(-50%); width: 700px; height: 400px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 100%, rgba(210,175,100,0.03) 0%, transparent 60%); }
.grain { position: fixed; inset: 0; z-index: 1; pointer-events: none; opacity: 0.5; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.12'/%3E%3C/svg%3E"); background-size: 180px 180px; }
.frame { position: fixed; inset: 28px; border: 1px solid rgba(210,175,100,0.08); z-index: 2; pointer-events: none; }
.frame::before, .frame::after { content: ''; position: absolute; width: 20px; height: 20px; }
.frame::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }
.frame::after { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner { position: fixed; inset: 28px; z-index: 2; pointer-events: none; }
.frame-inner::before, .frame-inner::after { content: ''; position: absolute; width: 20px; height: 20px; }
.frame-inner::before { top: -1px; right: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner::after { bottom: -1px; left: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }
.page-inner { position: relative; z-index: 10; max-width: 880px; margin: 0 auto; padding: 60px 40px 100px; }
.page-inner.wide { max-width: 980px; }
.wm-row { display: flex; align-items: center; gap: 14px; width: 100%; }
.wm-row.slim { margin-bottom: 20px; }
.wm-line { flex: 1; height: 1px; background: linear-gradient(90deg, transparent, rgba(210,175,100,0.2)); }
.wm-line.r { background: linear-gradient(90deg, rgba(210,175,100,0.2), transparent); }
.wm-text { font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 600; letter-spacing: 4px; color: rgba(210,175,100,0.5); text-transform: uppercase; white-space: nowrap; }
.seal { position: relative; display: flex; align-items: center; justify-content: center; width: 40px; height: 40px; }
.seal.small { width: 40px; height: 40px; margin-bottom: 16px; }
.seal-ring { position: absolute; inset: 0; border-radius: 50%; border: 1px solid rgba(210,175,100,0.2); animation: spinSlow 40s linear infinite; }
.seal-ring-dashed { position: absolute; inset: 4px; border-radius: 50%; border: 1px dashed rgba(210,175,100,0.15); animation: spinSlow 25s linear infinite reverse; }
@keyframes spinSlow { to { transform: rotate(360deg); } }
.seal-diamond { width: 10px; height: 10px; border: 1px solid rgba(210,175,100,0.7); transform: rotate(45deg); position: relative; z-index: 1; }
.seal-diamond::after { content: ''; position: absolute; inset: 2px; background: rgba(210,175,100,0.35); }

/* ── HEADER ── */
.admin-header { margin-bottom: 32px; }
.header-row { display: flex; align-items: center; gap: 16px; }
.page-title { font-family: 'Cormorant', serif; font-size: 40px; font-weight: 300; color: #edeae2; }
.page-title.left { text-align: left; }
.page-title em { font-style: italic; color: rgba(210,175,100,0.8); }
.pending-badge { display: inline-flex; align-items: center; justify-content: center; min-width: 28px; height: 28px; padding: 0 8px; border-radius: 50%; background: rgba(210,175,100,0.85); color: #070708; font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 600; }

/* ── SEARCH ── */
.search-box { display: flex; align-items: center; gap: 10px; border-bottom: 1px solid rgba(237,232,223,0.1); padding: 12px 4px; margin-bottom: 20px; color: rgba(210,175,100,0.4); }
.search-input { flex: 1; background: none; border: none; outline: none; font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 300; color: #edeae2; }
.search-input::placeholder { color: rgba(237,232,223,0.2); font-style: italic; }

/* ── TABS ── */
.tab-row { display: flex; gap: 4px; border-bottom: 1px solid rgba(210,175,100,0.1); margin-bottom: 24px; overflow-x: auto; }
.tab-btn { background: none; border: none; cursor: pointer; padding: 10px 16px; font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 600; letter-spacing: 1.5px; color: rgba(237,232,223,0.3); border-bottom: 2px solid transparent; white-space: nowrap; transition: color 0.2s, border-color 0.2s; }
.tab-btn--active { color: rgba(210,175,100,0.9); border-bottom-color: rgba(210,175,100,0.8); }

/* ── EMPTY ── */
.empty-block { display: flex; flex-direction: column; align-items: center; padding: 80px 0; }
.empty-diamond { width: 14px; height: 14px; border: 1px solid rgba(210,175,100,0.5); transform: rotate(45deg); margin-bottom: 20px; }
.empty-title { font-family: 'Cormorant', serif; font-style: italic; font-size: 22px; color: rgba(237,232,223,0.3); margin-bottom: 8px; }
.empty-text { font-family: 'Montserrat', sans-serif; font-size: 12px; color: rgba(237,232,223,0.2); }

/* ── CARDS ── */
.cards-list { display: flex; flex-direction: column; gap: 12px; }
.app-card { border: 1px solid rgba(210,175,100,0.1); background: rgba(210,175,100,0.03); }
.app-card--flagged { border-color: rgba(220,100,90,0.25); }
.app-card-head { display: flex; align-items: center; gap: 16px; width: 100%; background: none; border: none; cursor: pointer; padding: 16px 18px; text-align: left; }
.app-avatar { width: 48px; height: 48px; border-radius: 50%; border: 1px solid rgba(210,175,100,0.25); display: flex; align-items: center; justify-content: center; overflow: hidden; flex-shrink: 0; font-family: 'Cormorant', serif; font-size: 17px; color: rgba(210,175,100,0.5); }
.app-avatar img { width: 100%; height: 100%; object-fit: cover; }
.app-head-info { flex: 1; min-width: 0; }
.app-name { font-family: 'Cormorant', serif; font-size: 17px; color: #edeae2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.app-club { font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 1px; color: rgba(210,175,100,0.5); }
.app-club--missing { color: rgba(220,100,90,0.6); }
.status-chip { font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase; padding: 4px 10px; border: 1px solid; flex-shrink: 0; }
.status-chip--pending { color: rgba(210,175,100,0.6); border-color: rgba(210,175,100,0.3); }
.status-chip--approved { color: rgba(130,190,140,0.8); border-color: rgba(130,190,140,0.35); }
.status-chip--rejected { color: rgba(220,100,90,0.7); border-color: rgba(220,100,90,0.3); }
.chevron { color: rgba(210,175,100,0.3); flex-shrink: 0; transition: transform 0.2s; }
.chevron--open { transform: rotate(180deg); }

.app-card-body { border-top: 1px solid rgba(210,175,100,0.08); padding: 16px 18px 18px; }
.detail-grid { display: flex; flex-direction: column; gap: 8px; margin-bottom: 12px; }
.detail-row { display: flex; gap: 14px; font-family: 'Montserrat', sans-serif; font-size: 13px; }
.detail-label { width: 120px; flex-shrink: 0; font-size: 9px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(210,175,100,0.4); }
.detail-val { color: rgba(237,232,223,0.65); font-weight: 300; }
.why-block { margin-bottom: 14px; }
.why-text { font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 300; color: rgba(237,232,223,0.6); line-height: 1.7; margin-top: 6px; }
.nda-row { display: flex; align-items: center; gap: 8px; font-family: 'Montserrat', sans-serif; font-size: 11px; color: rgba(237,232,223,0.35); margin-bottom: 16px; }
.nda-ok { color: rgba(130,190,140,0.7); }
.nda-bad { color: rgba(220,100,90,0.6); }

.action-row { display: flex; gap: 10px; }
.btn-approve, .btn-reject { flex: 1; font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; padding: 12px; cursor: pointer; transition: opacity 0.2s; }
.btn-approve { background: rgba(210,175,100,0.85); border: none; color: #070708; }
.btn-reject { background: none; border: 1px solid rgba(220,100,90,0.35); color: rgba(220,100,90,0.75); }
.btn-approve:disabled, .btn-reject:disabled { opacity: 0.4; cursor: default; }
.resolved-row { font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; }
.resolved-row--approved { color: rgba(130,190,140,0.7); }
.resolved-row--rejected { color: rgba(220,100,90,0.65); }

.load-more-btn { align-self: center; margin-top: 10px; background: none; border: 1px solid rgba(210,175,100,0.25); padding: 10px 24px; font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 600; letter-spacing: 1.5px; color: rgba(210,175,100,0.6); cursor: pointer; }

/* ── CONFIRM DIALOG ── */
.overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.85); z-index: 100; display: flex; align-items: center; justify-content: center; padding: 24px; }
.confirm-panel { background: #0a0a0b; border: 1px solid rgba(210,175,100,0.2); max-width: 400px; width: 100%; padding: 28px; }
.confirm-title { font-family: 'Cormorant', serif; font-size: 22px; color: #edeae2; margin-bottom: 10px; }
.confirm-body { font-family: 'Montserrat', sans-serif; font-size: 13px; color: rgba(237,232,223,0.5); line-height: 1.6; margin-bottom: 24px; }
.confirm-body strong { color: rgba(210,175,100,0.8); font-weight: 600; }
.confirm-actions { display: flex; gap: 10px; }
.btn-back { flex: 1; background: none; border: 1px solid rgba(237,232,223,0.15); color: rgba(237,232,223,0.5); font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 1.5px; padding: 12px; cursor: pointer; }

/* ── SNACKBAR ── */
.snackbar { position: fixed; bottom: 32px; left: 50%; transform: translateX(-50%); background: #0a0a0b; border: 1px solid rgba(210,175,100,0.25); padding: 12px 24px; font-family: 'Montserrat', sans-serif; font-size: 12px; z-index: 200; }
.snackbar--success { border-color: rgba(130,190,140,0.4); color: rgba(130,190,140,0.85); }
.snackbar--error { border-color: rgba(220,100,90,0.4); color: rgba(220,100,90,0.85); }
.snackbar--info { color: rgba(210,175,100,0.8); }
.alert-slide-enter-active, .alert-slide-leave-active { transition: opacity 0.3s, transform 0.3s; }
.alert-slide-enter-from, .alert-slide-leave-to { opacity: 0; transform: translate(-50%, 10px); }

/* ── ADMIN TAB BAR ── */
.admin-tab-bar { position: fixed; bottom: 0; left: 0; right: 0; z-index: 50; display: flex; background: rgba(7,7,8,0.97); border-top: 1px solid rgba(210,175,100,0.12); padding: 10px 0 max(10px, env(safe-area-inset-bottom)); }
.tab-item { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 5px; background: none; border: none; cursor: pointer; color: rgba(237,232,223,0.25); padding: 6px 0; }
.tab-item span { font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase; }
.tab-item--active { color: rgba(210,175,100,0.85); }
</style>